/* eslint-disable react/display-name */
import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../concepts/resources/Icon';
import { DropButton } from '../../../../ui/DropButton';
import { Copy, FlexFrame, Spacer, Title } from '../../../dashboard/elements/LayoutElements';
import { Popup } from '../../../popups/Popup';
import { AutoScene } from '../Scene';
import { TabularDataDimension, TabularImportParams, TabularImportStepProps } from '../TabularImportModel';
import { Box, Button } from './../../../../ui';
import { MappingSelector } from './MappingSelector';
import { TabularImportStepPreviewGenerator } from './TabularImportStepPreview';

export interface TabularImportGroupingStepConfig {
    label: string;
    preview: TabularImportStepPreviewGenerator;
}

type GroupingFormProps = Pick<TabularImportStepProps, 'data' | 'params'> & { onUpdate: VoidCallback1<TabularImportParams> };
const GroupingForm = React.memo<GroupingFormProps>(({ data, params, onUpdate }) => {

    const dimensions = useMemo(() => TabularDataDimension.from(data), [data]);

    const onChange = useCallback((grouping: TabularDataDimension) => {
        onUpdate({ ...params, grouping });
    }, [params, onUpdate]);

    return <Box>
        <MappingSelector hasHeaders={params.hasHeaders} options={dimensions} value={params.grouping} onChange={onChange} />
        <Spacer f={1} />
    </Box>;
});


const PreviewFrame = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const PreviewScene = styled.div`
    position: relative;
    flex: 1 1 auto;

    ${DropButton} {
        position: absolute;
        top: 50%;        
        transform: translate(0, -50%);
        
        &.--left { left: 1rem; }
        &.--right { right: 1rem; }
    }
`;

type GroupingPreviewProps = Pick<TabularImportStepProps, 'data' | 'params'> & { config: TabularImportGroupingStepConfig };

const GroupingPreview = React.memo<GroupingPreviewProps>(({ config, data, params }) => {

    const [page, setPage] = useState(0);
    const { figures, pages } = config.preview(data, params, page);

    const infoText = pages > 0 ? `(${(page % pages) + 1} of ${pages})` : '';

    return <PreviewFrame>
        <Title>Preview {infoText}</Title>
        <PreviewScene>
            <AutoScene objects={figures} />
            <DropButton rounding="full" className="--left" onClick={() => setPage(x => x - 1)}>
                <Icon size="2" src="deqo://icons/hero/chevron-left" />
            </DropButton>
            <DropButton rounding="full" className="--right" onClick={() => setPage(x => x + 1)}>
                <Icon size="2" src="deqo://icons/hero/chevron-right" />
            </DropButton>
        </PreviewScene>
    </PreviewFrame>;
});


export const TabularImportGroupingStep = (config: TabularImportGroupingStepConfig) =>
    React.memo<TabularImportStepProps>(({ data, params, onStep }) => {

        const [state, setState] = useState(params);

        const onNext = useCallback(() => onStep(state, + 1), [onStep, state]);
        const onBack = useCallback(() => onStep(state, - 1), [onStep, state]);

        return <>
            <Popup.Content>
                <FlexFrame>
                    <Title>Grouping</Title>
                    <Copy>
                        Select a column to use for grouping:
                    </Copy>
                    <Spacer />
                    <GroupingForm data={data} params={params} onUpdate={setState} />
                    <Spacer />
                    <GroupingPreview config={config} data={data} params={state} />
                </FlexFrame>
            </Popup.Content>
            <Popup.Actions>
                <Button onAction={onBack}>
                    Back
                </Button>
                <Button variant="primary" onAction={onNext}>
                    Next
                </Button>
            </Popup.Actions>
        </>;
    });
